<div class="app-page">
  <!-- Header -->
  <div class="app-page-header">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="section-title">Gestión de Roles y Permisos</h1>
        <p class="section-subtitle">
          Administra los roles y permisos de los usuarios del sistema
        </p>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="app-page-content">
    <p-toast></p-toast>

    @if (!canEditRoles()) {
    <div class="mb-6 rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
      <div class="flex items-start gap-3">
        <svg class="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div class="flex-1">
          <h5 class="text-sm font-medium text-yellow-900 dark:text-yellow-300">Acceso Restringido</h5>
          <p class="mt-1 text-xs text-yellow-700 dark:text-yellow-400">
            Solo los administradores pueden gestionar roles y permisos
          </p>
        </div>
      </div>
    </div>
    }

    <!-- Filtro de búsqueda -->
    <div class="mb-6">
      <span class="p-input-icon-left w-full">
        <i class="pi pi-search text-body-secondary"></i>
        <input
          type="search"
          pInputText
          [(ngModel)]="filterValue"
          placeholder="Buscar por nombre o email..."
          class="w-full"
        />
      </span>
    </div>

    <!-- Tabla de Usuarios -->
    <div class="app-card">
      <p-table
        [value]="filteredUsuarios"
        [loading]="loading()"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 20, 30, 50]"
        styleClass="app-table-container"
        [globalFilterFields]="['name', 'email']"
      >
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 20%">Usuario</th>
            <th style="width: 25%">Email</th>
            <th style="width: 35%">Roles</th>
            <th style="width: 10%">Estado</th>
            <th style="width: 10%">Acciones</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-usuario>
          <tr>
            <td>
              <div class="flex flex-col">
                <span class="font-medium text-gray-900 dark:text-white">
                  {{ usuario.nombre || usuario.name || '-' }} {{ usuario.apellido || usuario.lastName || '' }}
                </span>
              </div>
            </td>
            <td>
              <span class="text-gray-700 dark:text-gray-300">{{ usuario.email || '-' }}</span>
            </td>
            <td>
              @if (isEditing(usuario.id)) {
                <form [formGroup]="usuarioForm" (ngSubmit)="saveRoles()">
                  <p-multiSelect
                    formControlName="roles"
                    [options]="rolesOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Seleccione roles"
                    [showToggleAll]="true"
                    [disabled]="!canEditRoles()"
                    styleClass="w-full"
                    class="w-full"
                  ></p-multiSelect>
                  <div class="mt-2 flex gap-2">
                    <button
                      pButton
                      type="submit"
                      label="Guardar"
                      icon="pi pi-check"
                      class="p-button-sm p-button-success"
                      [disabled]="loading() || usuarioForm.invalid"
                    ></button>
                    <button
                      pButton
                      type="button"
                      label="Cancelar"
                      icon="pi pi-times"
                      class="p-button-sm p-button-outlined"
                      (click)="cancelEdit()"
                      [disabled]="loading()"
                    ></button>
                  </div>
                </form>
              } @else {
                <div class="flex flex-wrap gap-2">
                  @if (usuario.roles && usuario.roles.length > 0) {
                    @for (role of usuario.roles; track role) {
                      <span class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
                        [ngClass]="{
                          'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': role === 'ROLE_AGENTE_PUBLICIDAD',
                          'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300': role === 'ROLE_ADMIN_PUBLICIDAD',
                          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': role === 'ROLE_COMPAGINADOR',
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': role === 'ROLE_AGENTE_FACTURACION',
                          'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300': role === 'ROLE_FACTURACION_AUDITORIA',
                          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': role === 'ROLE_SUPER_ADMIN'
                        }">
                        {{ getRoleLabel(role) }}
                      </span>
                    }
                  } @else {
                    <span class="text-gray-400 dark:text-gray-500 text-sm">Sin roles</span>
                  }
                </div>
              }
            </td>
            <td>
              @if (usuario.enabled) {
                <span class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  Activo
                </span>
              } @else {
                <span class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                  Inactivo
                </span>
              }
            </td>
            <td>
              @if (canEditRoles()) {
                @if (isEditing(usuario.id)) {
                  <span class="text-gray-400 text-sm">Editando...</span>
                } @else {
                  <button
                    pButton
                    type="button"
                    icon="pi pi-pencil"
                    class="p-button-sm p-button-outlined"
                    (click)="startEdit(usuario)"
                    [disabled]="loading()"
                    pTooltip="Editar roles"
                    tooltipPosition="top"
                  ></button>
                }
              } @else {
                <span class="text-gray-400 text-sm">-</span>
              }
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5" class="text-center py-8">
              <div class="flex flex-col items-center gap-2">
                <svg class="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p class="text-gray-500 dark:text-gray-400">No se encontraron usuarios</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- Información de Roles -->
    <div class="mt-6 app-card">
      <h3 class="mb-4 text-lg font-semibold" style="color: hsl(var(--text));">Roles Disponibles</h3>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        @for (role of rolesOptions; track role.value) {
          <div class="rounded-lg border p-4" style="border-color: hsl(var(--border)); background-color: hsl(var(--surface-2));">
            <div class="flex items-start gap-3">
              <span class="inline-flex h-2 w-2 rounded-full mt-1.5"
                [ngClass]="{
                  'bg-blue-500': role.value === 'ROLE_AGENTE_PUBLICIDAD',
                  'bg-purple-500': role.value === 'ROLE_ADMIN_PUBLICIDAD',
                  'bg-green-500': role.value === 'ROLE_COMPAGINADOR',
                  'bg-yellow-500': role.value === 'ROLE_AGENTE_FACTURACION',
                  'bg-orange-500': role.value === 'ROLE_FACTURACION_AUDITORIA',
                  'bg-red-500': role.value === 'ROLE_SUPER_ADMIN'
                }"></span>
              <div class="flex-1">
                <h4 class="font-medium text-sm" style="color: hsl(var(--text));">{{ role.label }}</h4>
                <p class="mt-1 text-xs" style="color: hsl(var(--muted));">
                  @switch (role.value) {
                    @case ('ROLE_AGENTE_PUBLICIDAD') {
                      Usuario con permisos para gestionar órdenes de publicidad
                    }
                    @case ('ROLE_ADMIN_PUBLICIDAD') {
                      Usuario con permisos para gestionar meta variables de publicidad
                    }
                    @case ('ROLE_COMPAGINADOR') {
                      Usuario con permisos para gestionar tandas, exportación y facturación
                    }
                    @case ('ROLE_AGENTE_FACTURACION') {
                      Usuario con permisos para realizar la facturación
                    }
                    @case ('ROLE_FACTURACION_AUDITORIA') {
                      Usuario con permisos para consultar datos de la facturación. Usado para auditoría UNL
                    }
                    @case ('ROLE_SUPER_ADMIN') {
                      Role destinado a gestión informática
                    }
                  }
                </p>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</div>

